import React from 'react';
import { Paper, Grid, TextField, Stack, Button, Autocomplete, Chip, Typography, Divider, Box } from '@mui/material';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useDebounce from '@/hook/useDebounce';

const defaultFilters = {
	email: '',
	eventAction: [],
	collection: [],
	operationType: '',
	startDate: null,
	endDate: null
};

const eventActionOptions = ['db-insert', 'db-update', 'db-delete', 'db-replace', 'system-update', 'user_logins'];
const operationTypeOptions = ['insert', 'update', 'replace', 'delete', 'login'];

export function AuditLogFilters({ filters = defaultFilters, onChange, collections = [] }) {
	const [localEmail, setLocalEmail] = React.useState(filters.email || '');
	const debouncedEmail = useDebounce(localEmail, 400);

	React.useEffect(() => {
		setLocalEmail(filters.email || '');
	}, [filters.email]);

	React.useEffect(() => {
		if (debouncedEmail !== filters.email && onChange) {
			onChange({ ...filters, email: debouncedEmail });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedEmail]);

	const handleChange = (key, value) => {
		if (!onChange) return;
		onChange({ ...filters, [key]: value });
	};

	const handleReset = () => {
		onChange?.({ ...defaultFilters });
		setLocalEmail('');
	};

	const startValue = filters.startDate ? new Date(filters.startDate) : null;
	const endValue = filters.endDate ? new Date(filters.endDate) : null;

	const collectionOptions = React.useMemo(() => {
		const unique = new Set([...(collections || []), ...(filters.collection || [])]);
		return Array.from(unique).filter(Boolean);
	}, [collections, filters.collection]);

	return (
		<motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
			<Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, mb: 2, bgcolor: 'background.paper' }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h6" fontWeight={700}>
						Audit Log Filters
					</Typography>
					<Button size="small" variant="text" color="secondary" onClick={handleReset}>
						Reset Filters
					</Button>
				</Stack>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={4}>
						<TextField
							fullWidth
							label="Email / Updated By"
							placeholder="user@email.com"
							value={localEmail}
							onChange={e => setLocalEmail(e.target.value)}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<Autocomplete
							multiple
							freeSolo
							options={eventActionOptions}
							value={filters.eventAction || []}
							onChange={(event, value) => handleChange('eventAction', value)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip key={`${option}-${index}`} label={option} {...getTagProps({ index })} />
								))
							}
							renderInput={params => <TextField {...params} label="Event Action" placeholder="db-update, cron-job-*" />}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<Autocomplete
							multiple
							freeSolo
							options={collectionOptions}
							value={filters.collection || []}
							onChange={(event, value) => handleChange('collection', value)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip key={`${option}-${index}`} label={option} {...getTagProps({ index })} />
								))
							}
							renderInput={params => <TextField {...params} label="Collection" placeholder="Users, AuditLogs, CronRuns" />}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<TextField
							select
							fullWidth
							label="Operation Type"
							SelectProps={{ native: true }}
							value={filters.operationType || ''}
							onChange={e => handleChange('operationType', e.target.value || '')}
						>
							<option value="">Any</option>
							{operationTypeOptions.map(option => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</TextField>
					</Grid>

					<Grid item xs={12} md={4}>
						<Stack direction="row" spacing={1}>
							<Box sx={{ flex: 1 }}>
								<DatePicker
									selected={startValue}
									onChange={date => {
										const next = { ...filters, startDate: date };
										if (date && endValue && endValue < date) {
											next.endDate = date;
										}
										onChange?.(next);
									}}
									selectsStart
									startDate={startValue}
									endDate={endValue}
									customInput={<TextField fullWidth label="From" />}
									isClearable
								/>
							</Box>
							<Box sx={{ flex: 1 }}>
								<DatePicker
									selected={endValue}
									onChange={date => {
										const next = { ...filters, endDate: date };
										if (filters.startDate && date && startValue && startValue > date) {
											next.startDate = date;
										}
										onChange?.(next);
									}}
									selectsEnd
									startDate={startValue}
									endDate={endValue}
									minDate={startValue}
									customInput={<TextField fullWidth label="To" />}
									isClearable
								/>
							</Box>
						</Stack>
					</Grid>

					<Grid item xs={12} md={4}>
						<Stack direction="row" justifyContent="flex-end" spacing={1}>
							<Button variant="outlined" onClick={handleReset}>
								Reset
							</Button>
							<Button variant="contained" onClick={() => onChange?.({ ...filters, email: localEmail })}>
								Apply
							</Button>
						</Stack>
					</Grid>
				</Grid>

				<Divider sx={{ mt: 2 }} />

				<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
					Filters auto-apply as you type. Use free text to search cron labels like cron-job-* or custom collections.
				</Typography>
			</Paper>
		</motion.div>
	);
}
